import React, { useState } from "react"
import {
	Button,
	Grid,
	Tab,
	Tabs,
	Typography
} from "@material-ui/core"
import {
	ActionDialog
} from "@/components"
import ApiService from "@/services/Api"
import { themeColors } from "letalkui"
import clsx from "clsx"

import { useGlobalStateStore } from "@/store/GlobalState"
import {
	TrendingUp as PriorityIcon,
	Message as QueueIcon,
	RadioButtonChecked as StatusIcon,
	Block as BlockIcon
} from "@material-ui/icons"
import useStyles from "@/pages/Admin/Dashboard/Metrics/ProductMessageMetrics/QueueMetrics/DetailedQueueMetrics/styles"
import { colors } from "@/styles/colors"
import ClearQueue from "@/pages/Admin/Dashboard/Metrics/ProductMessageMetrics/QueueMetrics/DetailedQueueMetrics/ClearQueue"
import { isSmallScreen } from "@/utils/checkDevice"
import ColorUtil from "@/utils/ColorUtil"

type ProductMessageProcessBlock = "inbox-message-queue-full" | "out-of-business-time" | "reached-daily-messages-limit"

type QueueMessageData = {
	count: number
	isBlocked: boolean
	isProductMessageQueueOverloaded: boolean
	blockReasons: ProductMessageProcessBlock[]
	priority: "high" | "medium" | "low"
}

type DetailedProductMessageQueueData = {
	chatBotMessages: QueueMessageData
	integrationMessages: QueueMessageData
	messageBlastMessages: QueueMessageData
	sendMessageBatch: QueueMessageData
	activeCampaignPluginAutomationMessages: QueueMessageData
	snoozeMessages: QueueMessageData
}

type ProductMessageQueueMetrics = DetailedProductMessageQueueData & {
	isProductMessageQueueOverloaded: boolean
}

type DetailedQueueProps = {
	disabled: boolean
	children: React.ReactNode
}

export type QueueName = "chatBotMessages" | "integrationMessages" | "messageBlastMessages" | "activeCampaignPluginAutomationMessages" | "snoozeMessages"

const webQueueNameToQueueData: Record<QueueName, { title: string, key: string }> = {
	chatBotMessages: {
		title: "ChatBot",
		key: "chatBot"
	},
	activeCampaignPluginAutomationMessages: {
		title: "Active Campaign",
		key: "activeCampaign"
	},
	integrationMessages: {
		title: "Integrações",
		key: "integration"
	},
	snoozeMessages: {
		title: "Soneca",
		key: "snoze"
	},
	messageBlastMessages: {
		title: "Envios em massa",
		key: "messageBlast"
	}
}

const queuePriorityToReadablePriority: Record<QueueMessageData["priority"], string> = {
	high: "Alta",
	medium: "Média",
	low: "Baixa"
}

const blockReasonToReadableReason: Record<ProductMessageProcessBlock, string> = {
	"inbox-message-queue-full": "Fila do inbox cheia",
	"out-of-business-time": "Fora do hórario comercial",
	"reached-daily-messages-limit": "Limite diario atingido"
}

const DetailedQueueMetrics: React.FC<DetailedQueueProps> = (props) => {
	const {
		disabled,
		children
	} = props

	const [openDialog, setOpenDialog] = useState(false)
	const [loading, setLoading] = useState(false)
	const [queueMetrics, setDetailedQueueMetrics] = useState<ProductMessageQueueMetrics>({} as ProductMessageQueueMetrics)
	const [selectedQueueTab, setSelectedQueueTab] = useState<QueueName>("chatBotMessages")

	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()
	const isWabaChannel = globalStateStore.instance.current_channel_type === "waba"

	const currentMessageMessageData = queueMetrics[selectedQueueTab] || ({} as QueueMessageData)
	const hasMultipleBlocks = currentMessageMessageData?.blockReasons?.length > 1

	const queueTabs = Object.entries(webQueueNameToQueueData).map(([name, { title, key }]) => ({
		name,
		title,
		key
	}))
	const wabaQueueNames = ["chatBotMessages", "messageBlastMessages"] as QueueName[]
	const wabaQueueTabs = queueTabs.filter(tab => wabaQueueNames.includes(tab.name as QueueName))
	const getRealTimeMetrics = async () => {
		setLoading(true)
		const { data } = await ApiService.get("/metrics/products/messages/queue/detailed")
		setDetailedQueueMetrics(data)
		setLoading(false)
	}

	const handleOpenDialog = async () => {
		await getRealTimeMetrics()
		setOpenDialog(true)
	}

	const handleCloseDialog = () => {
		setOpenDialog(false)
	}

	const handleChangeCurrentManagementTabType = (queueName: QueueName) => {
		setSelectedQueueTab(queueName)
	}

	return (
		<>
			<Grid
				onClick={disabled ? undefined : handleOpenDialog}
				{...(disabled && {
					style: {
						pointerEvents: "auto"
					}
				})}
			>
				{children}
			</Grid>
			<ActionDialog
				title="Métricas de envio de mensagens automáticas"
				openDialog={openDialog}
				loading={loading}
				onClose={handleCloseDialog}
				hideCloseButton
				maxWidth={"sm"}
				fullWidth
			>
				<Grid container direction={isSmallScreen ? "column" : "row"} spacing={2}>
					<Grid item xs={5}>
						<Tabs
							value={selectedQueueTab}
							onChange={(_, value) => handleChangeCurrentManagementTabType(value)}
							orientation={"vertical"}
							variant="scrollable"
							TabIndicatorProps={{
								style: {
									...ColorUtil.getBackgroundAndContrastColor(colors.getPrimaryColor()),
									left: 0,
									width: 3
								}
							}}
							classes={{
								root: classes.tabRoot
							}}
							className={classes.tabsContainer}
						>
							{isWabaChannel
								? wabaQueueTabs.map(wabaQueueTab => (
									<Tab
										key={wabaQueueTab.key}
										label={wabaQueueTab.title}
										value={wabaQueueTab.name}
										classes={{
											root: classes.tabRoot,
											selected: classes.tabSelected,
											wrapper: classes.tabWrapper
										}}
									/>))
								: queueTabs.map(managementTab => (
									<Tab
										key={managementTab.key}
										label={managementTab.title}
										value={managementTab.name}
										classes={{
											root: classes.tabRoot,
											selected: classes.tabSelected,
											wrapper: classes.tabWrapper
										}}
									/>
								))}
						</Tabs>
					</Grid>

					<Grid item xs={7}>
						<Grid container direction="column" style={{ gap: 24 }}>
							<Grid item>
								<Grid container style={{ gap: 10 }} alignItems="center">
									<Grid item className={classes.iconItem} style={{ ...ColorUtil.getBackgroundAndContrastColor(themeColors.colors.quaternary[50]) }}>
										<PriorityIcon htmlColor={themeColors.colors.quaternary[500]}/>
									</Grid>
									<Grid item>
										<Grid container direction="row" alignItems="center" style={{ gap: 5 }}>
											<Grid item>
												<Typography className={classes.titleText}>Prioridade:</Typography>
											</Grid>
											<Grid item>
												<Typography variant="h5" className={classes.titleText}>
													{queuePriorityToReadablePriority[currentMessageMessageData?.priority]}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>

							<Grid item>
								<Grid container style={{ gap: 10 }} alignItems="center">
									<Grid item className={classes.iconItem} style={{ ...ColorUtil.getBackgroundAndContrastColor(colors.green[100]) }}>
										<StatusIcon htmlColor={colors.green[500]}/>
									</Grid>
									<Grid item>
										<Grid container direction="row" alignItems="center" style={{ gap: 5 }}>
											<Grid item>
												<Typography>Status:</Typography>
											</Grid>
											<Grid item>
												<Typography variant="h5" className={classes.titleText}>
													{currentMessageMessageData?.blockReasons?.length > 0 ? "Inativa" : "Ativa"}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>

							{currentMessageMessageData?.blockReasons?.length > 0 && (
								<Grid item>
									<Grid container style={{ gap: 10 }} alignItems="flex-start">
										<Grid item className={classes.iconItem} style={{ ...ColorUtil.getBackgroundAndContrastColor(colors.red[100]) }}>
											<BlockIcon htmlColor={colors.red[400]} />
										</Grid>
										<Grid container direction="row" alignItems="center" style={{ gap: 5 }}>
											<Grid item>
												<Typography>Motivo do bloqueio:</Typography>
											</Grid>
											<Grid item>
												<Grid container
													className={clsx({
														[classes.multipleBlocks]: hasMultipleBlocks
													})}>
													{currentMessageMessageData?.blockReasons.map(blockReason => (
														<Grid item key={blockReason}>
															<Typography variant="h5">
																{blockReasonToReadableReason[blockReason]}
															</Typography>
														</Grid>
													))}
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							)}

							<Grid item>
								<Grid container style={{ gap: 10 }} alignItems="center">
									<Grid item className={classes.iconItem} style={{ ...ColorUtil.getBackgroundAndContrastColor(colors.purple[100]) }}>
										<QueueIcon htmlColor={colors.purple[500]} style={{ width: 22, height: 22 }} />
									</Grid>
									<Grid item>
										<Typography>Mensagens na Fila:</Typography>
									</Grid>
									<Grid item>
										<Typography variant="h5" className={classes.titleText}>
											{currentMessageMessageData?.count}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<ClearQueue disabled={loading} queueName={selectedQueueTab} queueNameLabel={webQueueNameToQueueData[selectedQueueTab].title}>
									<Button variant="contained" className={classes.buttonPrimaryColorOutLined}>
										Limpar Fila
									</Button>
								</ClearQueue>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</ActionDialog>

		</>
	)
}

export default DetailedQueueMetrics
